// Image optimization utilities

export interface ImageSource {
  src: string;
  type?: string;
  media?: string;
  sizes?: string;
}

export interface OptimizedImageConfig {
  src: string;
  alt: string;
  sources?: ImageSource[];
  placeholder?: string;
  priority?: boolean;
  sizes?: string;
}

// Helper to generate WebP sources with fallbacks
export const createOptimizedSources = (
  baseName: string,
  extension: string
): ImageSource[] => {
  return [
    {
      src: `${baseName}.webp`,
      type: 'image/webp',
    },
    {
      src: `${baseName}.${extension}`,
      type: `image/${extension === 'jpg' ? 'jpeg' : extension}`,
    },
  ];
};

// Generate responsive image sources for different screen sizes
export const createResponsiveSources = (
  baseName: string,
  extension: string,
  sizes: { width: number; descriptor: string }[]
): ImageSource[] => {
  const webpSources = sizes.map(({ width, descriptor }) => ({
    src: `${baseName}-${width}w.webp`,
    type: 'image/webp',
  }));

  const fallbackSources = sizes.map(({ width, descriptor }) => ({
    src: `${baseName}-${width}w.${extension}`,
    type: `image/${extension === 'jpg' ? 'jpeg' : extension}`,
  }));

  return [...webpSources, ...fallbackSources];
};

// Preload critical images
export const preloadImage = (src: string, as: 'image' = 'image') => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = src;
  document.head.appendChild(link);
};

// Preload multiple images
export const preloadImages = (images: string[]) => {
  images.forEach((src) => preloadImage(src));
};

// Generate blur placeholder (base64 encoded tiny image)
export const generatePlaceholder = (width: number = 10, height: number = 10, color: string = '#f0f0f0'): string => {
  // Create a simple SVG placeholder
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="${color}"/>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Image optimization configurations for HDF Hauling
export const imageConfigs: Record<string, OptimizedImageConfig> = {
  // Hero carousel images - high priority
  cloudstruck: {
    src: require('../images/cloudstruck.jpeg'),
    alt: 'HDF Hauling truck on the road with dramatic clouds',
    priority: true,
    placeholder: generatePlaceholder(),
  },
  newtruck: {
    src: require('../images/newtruck.png'),
    alt: 'Modern HDF Hauling truck ready for freight delivery',
    priority: true,
    placeholder: generatePlaceholder(),
  },
  twotruckhaul: {
    src: require('../images/twotruckhaul.jpeg'),
    alt: 'Two HDF Hauling trucks demonstrating fleet capacity',
    priority: true,
    placeholder: generatePlaceholder(),
  },

  // Service images - lazy loaded
  businesscard: {
    src: require('../images/businesscard.png'),
    alt: 'HDF Hauling business card showing professional fleet management services',
    priority: false,
    placeholder: generatePlaceholder(),
  },
  newtruckstone: {
    src: require('../images/newtruckstone.jpg'),
    alt: 'Professional hauling truck at stone quarry demonstrating cargo capabilities',
    priority: false,
    placeholder: generatePlaceholder(),
  },
  logohdf: {
    src: require('../images/logohdf.jpg'),
    alt: 'HDF Hauling company logo representing professional trucking services',
    priority: false,
    placeholder: generatePlaceholder(),
  },

  // Certification and company logos
  nclogo: {
    src: require('../images/nclogo.jpg'),
    alt: 'North Carolina certified trucking company certification logo',
    priority: false,
    placeholder: generatePlaceholder(),
  },

  // Additional service images
  dispatch: {
    src: require('../images/dispatch.webp'),
    alt: 'Professional dispatch services coordination center',
    priority: false,
    placeholder: generatePlaceholder(),
  },
  handshake: {
    src: require('../images/handshake.webp'),
    alt: 'Professional handshake representing HDF Hauling partnerships',
    priority: false,
    placeholder: generatePlaceholder(),
  },
  haulingtruck: {
    src: require('../images/haulingtruck.webp'),
    alt: 'HDF Hauling truck demonstrating reliable freight transportation',
    priority: false,
    placeholder: generatePlaceholder(),
  },
  tankhaul: {
    src: require('../images/tankhaul.jpeg'),
    alt: 'Specialized tank hauling services by HDF Hauling',
    priority: false,
    placeholder: generatePlaceholder(),
  },
  tractorhaul: {
    src: require('../images/tractorhaul.jpeg'),
    alt: 'Heavy equipment hauling services including tractors',
    priority: false,
    placeholder: generatePlaceholder(),
  },
  newtruckload: {
    src: require('../images/newtruckload.jpg'),
    alt: 'Loaded HDF Hauling truck ready for long-distance delivery',
    priority: false,
    placeholder: generatePlaceholder(),
  },
};

// Get optimized image config by key
export const getImageConfig = (key: string): OptimizedImageConfig | null => {
  return imageConfigs[key] || null;
};